import React from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Select, MenuProps } from "antd";
import { SUPPORTED_LANGUAGES } from "../../constants/languages";
import { TranslateIcon } from "./TranslateIcon";
import "./LanguageSelector.css";

interface LanguageSelectorProps {
  theme?: "light" | "dark";
  className?: string;
  compact?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  theme = "light",
  className = "",
  compact = true,
}) => {
  const { i18n } = useTranslation();

  const currentLang = (i18n.language || "en").split("-")[0].toLowerCase();

  const handleLanguageChange = (selectedLang: string) => {
    i18n.changeLanguage(selectedLang);
    try {
      localStorage.setItem("language", selectedLang);
      localStorage.setItem("kredafi-lang", selectedLang);
      document.documentElement.lang = selectedLang;
      document.documentElement.dir = selectedLang === "ar" ? "rtl" : "ltr";
    } catch (e) {
      // Ignore storage errors
    }
  };

  const isDark = theme === "dark";
  const currentLangObj =
    SUPPORTED_LANGUAGES.find((l) => l.code === currentLang) ||
    SUPPORTED_LANGUAGES[0];

  if (compact) {
    const items: MenuProps["items"] = SUPPORTED_LANGUAGES.map((lang) => ({
      key: lang.code,
      label: (
        <span className="languageSelectorOption">
          <span className="languageSelectorFlag">{lang.flag}</span>
          <span className="languageSelectorName">{lang.nativeName}</span>
          {lang.code === currentLang && <span className="languageSelectorCheck">✓</span>}
        </span>
      ),
      onClick: () => handleLanguageChange(lang.code),
    }));

    return (
      <Dropdown
        menu={{
          items,
          selectedKeys: [currentLang],
          style: {
            maxHeight: "320px",
            overflowY: "auto",
            borderRadius: "14px",
            padding: "6px",
            minWidth: "170px",
            boxShadow: isDark
              ? "0 16px 36px rgba(0, 0, 0, 0.65)"
              : "0 12px 28px rgba(0, 0, 0, 0.12)",
            background: isDark ? "#161b22" : "#ffffff",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.15)"
              : "1px solid rgba(0, 0, 0, 0.08)",
          },
        }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          className={`compactLangBtn compactFlagBtn ${
            isDark ? "compactLangBtnDark compactFlagBtnDark" : "compactLangBtnLight compactFlagBtnLight"
          } ${className}`}
          title={currentLangObj.nativeName}
          aria-label="Select Language"
        >
          <TranslateIcon size={30} className="compactTranslateIcon" />
        </button>
      </Dropdown>
    );
  }

  return (
    <div
      className={`languageSelectorWrapper ${
        isDark ? "languageSelectorDark" : "languageSelectorLight"
      } ${className}`}
    >
      <TranslateIcon size={18} className="languageSelectorGlobe" />
      <Select
        value={currentLang}
        onChange={handleLanguageChange}
        className="languageSelectorSelect"
        bordered={false}
        options={SUPPORTED_LANGUAGES.map((lang) => ({
          value: lang.code,
          label: (
            <span className="languageSelectorOption">
              <span className="languageSelectorFlag">{lang.flag}</span>
              <span>{lang.nativeName}</span>
            </span>
          ),
        }))}
      />
    </div>
  );
};

export default LanguageSelector;
